import { InputType } from '@import/enums/input-type.enum';
import { ColumnType } from '@import/interfaces';

/**
 *  Upload worksheets are parsed as this Column class
 *  'Column' represents a column from the worksheet
 */
export class Column {
    // Remove `type` when importer enhancement is complete
    // `type` is currently required for original enhancement
    type: ColumnType = null;
    values: Set<string>;
    valueMapping: Map<string, string> = new Map<string, string>();

    constructor(
        public name: string,
        public index: number
    ) {}

    /**
     * A simple helper to create multiple columns from a single array of strings. This is useful mostly for the initial upload
     * where the column names are a string array.
     */
    static fromArray(columnNames: string[]): Column[] {
        return columnNames
            .map((name: string, index: number) => {
                return new Column(name, index);
            })
            .filter((column: Column) => !!column);
    }

    setValues(rows: string[][]): void {
        const colValue = new Set<string>();

        rows.forEach((row: string[]): void => {
            const value = row[this.index];

            if (value && value.trim()) {
                colValue.add(value.trim());
            }
        });

        this.values = colValue;
    }

    getDisplayValues(limit?: number): string {
        let values = Array.from(this.values).filter((value) => {
            return value != null;
        });

        if (values.length === 0) {
            return 'Empty column';
        }

        if (limit) {
            values = values.splice(0, limit);
        }

        return values.join(', ');
    }

    isEmpty(): boolean {
        return this.values.size === 0;
    }

    isMapped(): boolean {
        return !!this.type;
    }

    hasValueMapped(value: string): boolean {
        const mappedValue = this.valueMapping.get(value);
        return mappedValue !== null && mappedValue !== undefined;
    }

    canMapValues(): boolean {
        return (
            this.isMapped() &&
            (this.type.input === InputType.options || this.type.input === InputType.boolean) &&
            this.values.size > 0
        );
    }
}
